// Shared staff controls: barber/date/time picker (staff availability + manual time) and the NEEDS_CONFIRM dialog.
import { useEffect, useState } from 'react';
import type { Api } from '../api/client.ts';
import type { PublicShop, TimeSlot } from '../api/types.ts';
import { message } from '../lib/errors.ts';
import { isoAt, timeOf } from '../lib/time.ts';
import { Dialog, Field, Loading } from '../ui/index.tsx';

export interface SlotChoice {
  barberId: string;
  startAt: string;
}

export function StaffSlotPicker({ api, shop, serviceId, barberIds, fixedBarberId, initialBarberId, initialDate, onChange }: {
  api: Api; shop: PublicShop; serviceId: string; barberIds: string[]; fixedBarberId?: string | null;
  initialBarberId?: string; initialDate: string; onChange: (c: SlotChoice | null) => void;
}) {
  const [barberId, setBarberId] = useState(fixedBarberId ?? initialBarberId ?? barberIds[0] ?? '');
  const [date, setDate] = useState(initialDate);
  const [times, setTimes] = useState<TimeSlot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [manual, setManual] = useState('');

  useEffect(() => {
    if (!barberId || !date) return;
    let alive = true;
    setTimes(null);
    setPicked(null);
    setError(null);
    onChange(null);
    api.availableTimes({ serviceId, barberId, date, mode: 'staff' })
      .then((t) => { if (alive) setTimes(t); })
      .catch((e) => { if (alive) { setError(message(e)); setTimes([]); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, serviceId, barberId, date]);

  const choose = (start: string) => { setPicked(start); setManual(''); onChange({ barberId, startAt: start }); };
  const chooseManual = (hhmm: string) => {
    setManual(hhmm);
    setPicked(null);
    onChange(/^\d{2}:\d{2}$/.test(hhmm) ? { barberId, startAt: isoAt(date, hhmm) } : null);
  };
  const name = (id: string) => shop.barbers.find((b) => b.id === id)?.name ?? id;

  return (
    <div>
      {!fixedBarberId && (
        <Field label="ช่าง">
          <select value={barberId} onChange={(e) => setBarberId(e.target.value)}>
            {barberIds.map((id) => <option key={id} value={id}>{name(id)}</option>)}
          </select>
        </Field>
      )}
      <Field label="วันที่">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <p className="field-msg" style={{ marginBottom: 8 }}>เวลาว่าง</p>
      {error && <p className="field-msg error">{error}</p>}
      {times === null ? <Loading text="กำลังโหลดเวลาว่าง…" /> : times.length === 0
        ? <p className="muted small" style={{ marginBottom: 12 }}>ไม่มีเวลาว่างตามตารางงาน (กำหนดเวลาเองได้ด้านล่าง)</p>
        : (
          <div className="time-grid" style={{ marginBottom: 12 }}>
            {times.map((t) => (
              <button key={t.start} type="button" className={`time-cell${picked === t.start ? ' selected' : ''}`} onClick={() => choose(t.start)}>
                {timeOf(t.start)}
              </button>
            ))}
          </div>
        )}
      <Field label="หรือกำหนดเวลาเอง" hint="ทีละ 5 นาที เช่น 18:45">
        <input type="time" step={300} value={manual} onChange={(e) => chooseManual(e.target.value)} />
      </Field>
    </div>
  );
}

/** Text for NEEDS_CONFIRM details. */
export function needsConfirmText(d: { outsideHours?: boolean; timeOff?: boolean }): string {
  const parts: string[] = [];
  if (d.outsideHours) parts.push('เวลานี้อยู่นอกเวลาทำงานของช่าง');
  if (d.timeOff) parts.push('เวลานี้ตรงกับวันหยุด/ลาของช่างหรือร้าน');
  return `${parts.join(' และ')} ยืนยันหรือไม่?`;
}

export function NeedsConfirmDialog({ details, onConfirm, onCancel, loading }: {
  details: { outsideHours?: boolean; timeOff?: boolean } | null; onConfirm: () => void; onCancel: () => void; loading?: boolean;
}) {
  return (
    <Dialog open={details !== null} title="ต้องยืนยันก่อนดำเนินการ" onConfirm={onConfirm} onCancel={onCancel} loading={loading}>
      {details && <p>{needsConfirmText(details)}</p>}
    </Dialog>
  );
}
